// Store RUNTIME de scénarios chargés depuis le BLOB (via l'API privée).
// Le contenu de jeu (scènes/chats/quiz) vient du JSON blob de l'histoire, pas
// des données bundlées. getScenarioForMission() (index.ts) consulte ce store
// en priorité, ce qui bascule TOUT le moteur (ChapterDetail, SceneReader,
// MissionCards, TaskDetail) sur le contenu du blob.

import { loadScenarioFromJsonPayload, type ScenarioChapter } from './storyLoader';

export interface RuntimeCharacter {
  id?: string;
  name: string;
  role?: string;
  color?: string;
  avatar?: string;
  avatarUrl?: string;
}

export interface RuntimeScenario {
  testMode: boolean;
  id: string;
  missionId: number;
  title: string;
  certification: string;
  subtitle?: string;
  chapters: ScenarioChapter[];
  characters: RuntimeCharacter[];
  /** CCT-130 — musique par défaut de la mission, servie à la RACINE du scénario. */
  music?: string;
}

// Cover par défaut (le type EgoCrisisScenario exige un coverImage require()).
const FALLBACK_COVER = undefined;   // CCT-253 : la couverture vient de `mission.coverImage`

const _store = new Map<number, RuntimeScenario>();
const _inFlight = new Map<number, Promise<void>>();

export function getRuntimeScenario(missionId: number): RuntimeScenario | undefined {
  return _store.get(missionId);
}

/** Construit + mémorise un scénario runtime à partir du doc blob {schemaVersion, scenario}. */
export function setRuntimeScenarioFromDoc(missionId: number, doc: any): RuntimeScenario | undefined {
  const sc = doc?.scenario ?? doc;
  if (!sc || typeof sc !== 'object') return undefined;
  const loaded = loadScenarioFromJsonPayload(
    {
      id: typeof sc.id === 'string' ? sc.id : `mission-${missionId}`,
      title: typeof sc.title === 'string' ? sc.title : 'Histoire',
      subtitle: typeof sc.subtitle === 'string' ? sc.subtitle : '',
      certification: typeof sc.certification === 'string' ? sc.certification : '',
      coverImage: FALLBACK_COVER,
    },
    doc,
  );
  const characters: RuntimeCharacter[] = Array.isArray(sc.characters)
    ? sc.characters.filter((c: any) => c && typeof c.name === 'string')
    : [];
  const runtime: RuntimeScenario = {
    testMode: sc.testMode === true,
    id: loaded.id,
    missionId,
    title: loaded.title,
    certification: loaded.certification,
    subtitle: loaded.subtitle,
    chapters: loaded.chapters,
    characters,
    // Le store ne gardait que les champs qu'il connaissait : la piste de la mission était
    // jetée ici, donc l'app jouait la musique bundlée d'une autre histoire (CCT-135).
    music: typeof sc.music === 'string' ? sc.music : undefined,
  };
  _store.set(missionId, runtime);
  return runtime;
}

/** Résout un personnage du scénario blob par nom (exact, prénom, ou id). */
export function getRuntimeCharacter(missionId: number, nameOrId: string): RuntimeCharacter | undefined {
  const sc = _store.get(missionId);
  if (!sc || !nameOrId) return undefined;
  const q = nameOrId.trim().toLowerCase();
  const chars = sc.characters;
  return (
    chars.find(c => c.name.toLowerCase() === q || (c.id ?? '').toLowerCase() === q)
    ?? chars.find(c => c.name.toLowerCase().split(' ')[0] === q.split(' ')[0])
  );
}

/**
 * Charge le scénario du blob pour la mission via `fetcher` (typiquement
 * apiFetch('/me/stories/{id}/scenario')). `force` re-fetch même si déjà en cache.
 * Best-effort : en cas d'échec on garde le repli local (SCENARIO_MAP).
 */
export async function loadRuntimeScenario(
  missionId: number,
  fetcher: () => Promise<any>,
  force = false,
): Promise<void> {
  const pending = _inFlight.get(missionId);
  if (pending) return pending;
  if (_store.has(missionId) && !force) return;
  const request = (async () => {
    const res = await fetcher();
    const doc = res?.doc ?? res;
    if (doc) setRuntimeScenarioFromDoc(missionId, doc);
  })();
  _inFlight.set(missionId, request);
  try {
    await request;
  } finally {
    _inFlight.delete(missionId);
  }
}
